/**
 * La matriz anual de cobros.
 *
 * Filas: consultorios. Columnas: los doce meses. Cada celda cruza **lo que
 * debería haber pasado** con **lo que pasó**, y ninguna se explica solo por su
 * color: todas llevan letra, símbolo y motivo al pasar el puntero. Quien no
 * distingue verde de rojo tiene que poder usar esta pantalla igual.
 *
 * Una celda **no es una factura**: un consultorio en un mes puede tener varios
 * pagadores y varios documentos. Al tocarla se abre un popover compacto para
 * los cambios rápidos; el detalle completo sigue un clic más allá.
 *
 * ── CB-11 · Los ejes, y por qué son tres modos y no dos tablas ──────────────
 *
 * El pedido era «una tabla para lo que está pago y otra para lo que está
 * facturado». Dos grillas de 35 × 12 obligan a cruzar 840 celdas contra otras
 * 840 a ojo, y la combinación **es** lo accionable: pagado y facturado está
 * resuelto; pagado sin facturar falta facturar; facturado sin pagar falta
 * cobrar. Así que es **una sola grilla con conmutador de eje**, el mismo
 * `.segmented` de la galería de consultorios y de Personal — no hay un tercer
 * patrón de conmutación en el admin.
 *
 *   Pago         pinta solo el eje de pago
 *   Facturación  pinta solo el eje de facturación
 *   Ambos        pinta los dos — es el modo por defecto (P3.1)
 *
 * En modo Pago el color **no puede depender de la facturación**, y viceversa.
 * Antes `cellTone()` era una cascada de prioridades entre los dos ejes, y por
 * eso un mes al día de pago se pintaba rojo por facturación. Ahora cada eje
 * tiene su función de tono y la cascada vive solo en `combinedTone`, que es el
 * único lugar donde mezclar es lo que se pide.
 *
 * ── Dónde quedó cada señal ──────────────────────────────────────────────────
 *
 *   Vencido                        modificador del eje de pago (`overdue`)
 *   Histórico incierto             eje de procedencia (`certainty`), CB-10
 *   Cobrado, falta factura fiscal  combinación de los dos ejes: solo en Ambos
 *   Falta la factura fiscal        modificador del eje de facturación
 *   Discrepancias                  bandera (`≠`), como ya era
 *   Cobro anulado                  eje de facturación (ver `BILLING_META`)
 *
 * Ninguna dejó de mostrarse: cada una tiene su marca textual y su `title` en
 * el modo que le corresponde.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  periodLabel, periodLabelShort, today, UNIT_STATUS_LABEL, UNIT_STATUS_TONE,
  type Provenance, type UnitStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Badge, Button, Card, Empty, Field, Modal, Money, Note, Table, TableSkeleton,
} from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';
import { ManualPaymentModal } from './Payments';
import { DecisionModal } from './Reconciliation';
import type { Tone } from '@cleanblue/shared';
import '@/styles/cobros.css';

type BillingState = 'NO_FACTURADO' | 'FACTURADO' | 'ANULADO';
type PaymentState = 'NO_APLICA' | 'PENDIENTE' | 'EN_REVISION' | 'PARCIAL' | 'PAGADO';
type Certainty = 'CIERTO' | 'INCIERTO';

interface Cell {
  period: string;
  billing_state: BillingState;
  payment_state: PaymentState;
  /** Modificador del eje de pago: se pasó la fecha y el cobro sigue abierto. */
  overdue: boolean;
  /** Eje de procedencia: con qué fuerza se sabe lo que dice la celda. */
  certainty: Certainty;
  /** Modificador del eje de facturación: correspondía cobrar y no se generó el cobro. */
  charge_missing: boolean;
  /** Modificador del eje de facturación: el cobro existe y falta el documento fiscal. */
  invoice_missing: boolean;
  discrepancy: boolean;
  expected_minor: number | null;
  actual_minor: number | null;
  paid_minor: number;
  charge_id: string | null;
  subscription_id: string | null;
  payer_count: number;
  invoice_count: number;
  invoice_issued: boolean;
  invoice_required: boolean;
  note: string | null;
}

/**
 * La combinación accionable: se cobró y falta la factura fiscal real.
 *
 * Es **el cruce de los dos ejes**, no un estado de ninguno de los dos — por
 * eso solo se pinta en el modo Ambos. En modo Pago esa misma celda es
 * «Pagado» y nada más; en modo Facturación es «Falta la factura fiscal».
 */
function needsInvoice(cell: Cell): boolean {
  return cell.invoice_missing
    && (cell.payment_state === 'PAGADO' || cell.payment_state === 'PARCIAL');
}

interface Row {
  unit_id: string; unit_code: string; unit_name: string | null;
  location_name: string | null; status: string; cells: Cell[]; attention: number;
}

interface MatrixSummary {
  billing: Partial<Record<BillingState, number>>;
  payment: Partial<Record<PaymentState, number>>;
  overdue: number;
  invoice_missing: number;
  uncertain: number;
  discrepancies: number;
  needs_invoice: number;
}

/** Cuántas filas habría del otro lado de cada filtro. Ver `MatrixCounts` en el worker. */
interface MatrixCounts { active: number; all: number; attention: number }

interface Matrix {
  year: number; months: string[]; rows: Row[]; summary: MatrixSummary; counts: MatrixCounts;
  first_year: number;
}

/* ── Los filtros de la barra ─────────────────────────────────────────────────
 *
 * Eran dos casillas de verificación y al dueño no le gustaron (13-sep-2026).
 * Son dos controles de naturaleza distinta, y cada uno toma el idioma que la
 * app ya tiene para su caso:
 *
 *   · **Estado** — la misma píldora cerrada de la galería de consultorios
 *     (`.chip-filtro` → `.segmented--status`), con la selección vigente y su
 *     contador; se despliega al tocarla y se cierra al elegir (dueño,
 *     3-sep-2026). Solo dos opciones: para cobrar, la pregunta es «activos o
 *     todos», no las cuatro de la galería.
 *   · **Necesita atención** — un interruptor, no un filtro con opciones. Misma
 *     familia de píldora, apagada en neutro y prendida en el tono de alerta,
 *     porque prendido significa «estoy mirando solo problemas». */
type StatusScope = 'ALL' | 'ACTIVE';

const STATUS_SCOPES: { key: StatusScope; label: string; tone: Tone | null }[] = [
  { key: 'ALL', label: 'Todos', tone: null },
  { key: 'ACTIVE', label: 'Activos', tone: UNIT_STATUS_TONE.ACTIVE },
];

const ACTIVE_SCOPE_HINT =
  'Un consultorio inactivo con algo pendiente igual aparece: nada que se deba desaparece por dejar de estar activo.';

/* ── Los tres modos ───────────────────────────────────────────────────────── */

type MatrixMode = 'payment' | 'billing' | 'both';

/** Misma convención de clave que `cleanblue:staff-view` y la densidad de la galería. */
const MODE_KEY = 'cleanblue:matrix-mode';

const MODE_OPTIONS: { key: MatrixMode; label: string }[] = [
  { key: 'payment', label: 'Pago' },
  { key: 'billing', label: 'Facturación' },
  { key: 'both', label: 'Ambos' },
];

function storedMode(): MatrixMode {
  const saved = localStorage.getItem(MODE_KEY);
  // «Ambos» por defecto, decidido en P3.1: es el modo que sostiene la acción,
  // porque lo que hay que hacer sale de la combinación, no de un eje solo.
  return saved === 'payment' || saved === 'billing' || saved === 'both' ? saved : 'both';
}

/**
 * Etiqueta, símbolo y tono de cada eje, por separado.
 *
 * **«Cobro anulado» vive en el eje de facturación, y es una decisión, no una
 * herencia.** Es `charges.status = 'VOID'`: un *cobro* anulado, no una factura
 * anulada — las 16 celdas de 2026 son el 616, que nunca inició el servicio
 * recurrente, y el 604, cuyo servicio terminó el 2025-12-31. Va acá porque el
 * eje de facturación es justamente el ciclo de vida de `charges`
 * (`DRAFT → ISSUED → VOID`), y porque los otros dos ejes lo rechazan: no es un
 * estado de pago (no hay nada que pagar) ni de procedencia (se sabe
 * exactamente qué pasó, con motivo y fecha de anulación). Y no se colapsa con
 * «No facturado»: «nunca se generó» y «se generó y se revocó» son hechos
 * distintos que el 616 y el 604 vuelven visibles.
 *
 * La etiqueta dice **«Cobro anulado»** y no «Anulado» a secas por lo que
 * documenta CB-26: en este dominio «anulado» también se dice de una factura
 * que una nota de crédito revierte, que es otra cosa y no se representa acá.
 */
const BILLING_META: Record<BillingState, { label: string; mark: string; tone: Tone }> = {
  NO_FACTURADO: { label: 'No facturado', mark: '–', tone: 'neutral' },
  FACTURADO: { label: 'Facturado', mark: 'F', tone: 'info' },
  ANULADO: { label: 'Cobro anulado', mark: '⊘', tone: 'neutral' },
};

/**
 * Los estados de pago **excluyentes**, y solo esos.
 *
 * Eran ocho entradas en la leyenda y no eran comparables entre sí. «Vencido»
 * se fue a modificador, «histórico incierto» al eje de procedencia y «cobrado,
 * falta factura fiscal» a la combinación. Lo que queda son estados de los que
 * exactamente uno es cierto a la vez.
 */
const PAYMENT_META: Record<PaymentState, { label: string; mark: string; tone: Tone }> = {
  NO_APLICA: { label: 'No aplica', mark: '–', tone: 'neutral' },
  PENDIENTE: { label: 'Pendiente', mark: '·', tone: 'warn' },
  PARCIAL: { label: 'Pago parcial', mark: '½', tone: 'warn' },
  PAGADO: { label: 'Pagado', mark: '✓', tone: 'ok' },
  EN_REVISION: { label: 'En revisión', mark: '◔', tone: 'info' },
};

/** Las señales que no son estados: modificadores, procedencia y banderas. */
const OVERDUE_MARK = '!';
const UNCERTAIN_MARK = '?';
/** El punto pegado a la marca significa siempre «falta el documento fiscal». */
const NO_INVOICE_SUFFIX = '·';

const TONE_STYLE: Record<Tone, { background: string; color: string; borderColor: string }> = {
  ok: { background: 'var(--ok-bg)', color: 'var(--ok)', borderColor: 'var(--ok-line)' },
  warn: { background: 'var(--warn-bg)', color: 'var(--warn)', borderColor: 'var(--warn-line)' },
  danger: { background: 'var(--danger-bg)', color: 'var(--danger)', borderColor: 'var(--danger-line)' },
  info: { background: 'var(--blue-50)', color: 'var(--blue-700)', borderColor: 'var(--blue-200)' },
  neutral: { background: '#f4f6f9', color: 'var(--muted-2)', borderColor: 'var(--line)' },
};

/**
 * El tono del eje de pago. **No mira la facturación.** Vencido es el
 * modificador que sube pendiente y parcial de «hay que cobrar» a «se pasó la
 * fecha», que es el único salto de urgencia que el eje de pago admite.
 */
function paymentTone(cell: Cell): Tone {
  if (cell.overdue) return 'danger';
  return PAYMENT_META[cell.payment_state].tone;
}

/**
 * El tono del eje de facturación. **No mira el pago.** Que falte el cargo es
 * lo urgente —un mes que nadie facturó no se cobra nunca—; que falte la
 * factura fiscal de un cobro ya emitido es una tarea pendiente, no una pérdida.
 */
function billingTone(cell: Cell): Tone {
  if (cell.billing_state === 'ANULADO') return 'neutral';
  if (cell.billing_state === 'NO_FACTURADO') return cell.charge_missing ? 'danger' : 'neutral';
  return cell.invoice_missing ? 'warn' : 'info';
}

/**
 * El tono del modo Ambos: la cascada, y acá sí es lo correcto.
 *
 * Mezclar los ejes es exactamente lo que este modo promete —leer urgencia de
 * un vistazo— y por eso la cascada se quedó en un solo lugar en vez de estar
 * repartida en el render. Lo que cambió es que ya no lee el eje de pago para
 * decidir cosas de facturación: «falta el cargo» ahora es `charge_missing`, un
 * hecho de facturación, y no la coincidencia `NO_FACTURADO` + `PENDIENTE`.
 */
function combinedTone(cell: Cell): Tone {
  if (cell.discrepancy) return 'danger';
  if (cell.billing_state === 'ANULADO') return 'neutral';
  if (cell.charge_missing) return 'danger';
  if (needsInvoice(cell)) return 'warn';
  return paymentTone(cell);
}

function cellTone(cell: Cell, mode: MatrixMode): Tone {
  if (mode === 'payment') return paymentTone(cell);
  if (mode === 'billing') return billingTone(cell);
  return combinedTone(cell);
}

/** La marca del eje de facturación, con su modificador de documento fiscal. */
function billingMark(cell: Cell): string {
  if (cell.billing_state === 'NO_FACTURADO') return cell.charge_missing ? '✗' : '–';
  const meta = BILLING_META[cell.billing_state];
  return cell.invoice_missing ? `${meta.mark}${NO_INVOICE_SUFFIX}` : meta.mark;
}

/**
 * El símbolo grande de la celda, según el eje que se esté mirando.
 *
 * En Ambos, y solo en Ambos, aparece `✓·`: la combinación «cobrado, falta
 * factura fiscal». Es la señal que antes ocupaba un estado de pago propio y
 * 60 celdas.
 */
function cellMark(cell: Cell, mode: MatrixMode): string {
  if (mode === 'billing') return billingMark(cell);
  if (mode === 'payment') return PAYMENT_META[cell.payment_state].mark;
  if (cell.billing_state === 'ANULADO') return BILLING_META.ANULADO.mark;
  if (cell.charge_missing) return '✗';
  if (needsInvoice(cell)) return `${PAYMENT_META[cell.payment_state].mark}${NO_INVOICE_SUFFIX}`;
  return PAYMENT_META[cell.payment_state].mark;
}

/**
 * El badge de la esquina superior izquierda: la señal que no es un estado.
 *
 * La procedencia gana sobre el vencimiento porque se contradicen: un mes
 * incierto no es una deuda todavía, así que llamarlo vencido sería afirmar
 * que ya se le puede cobrar a alguien. En la práctica no compiten — el
 * dominio no marca vencido lo que no está `ISSUED` — pero el orden deja
 * escrito cuál manda si algún día lo hicieran.
 */
function cellModifier(cell: Cell, mode: MatrixMode): { mark: string; label: string } | null {
  if (cell.certainty === 'INCIERTO') {
    return { mark: UNCERTAIN_MARK, label: 'Sin evidencia suficiente: espera revisión' };
  }
  if (cell.overdue && mode !== 'billing') {
    return { mark: OVERDUE_MARK, label: 'Vencido' };
  }
  return null;
}

/** `Note` no tiene variante neutra: lo que no es bueno ni urgente se dice en azul. */
function noteTone(tone: Tone): 'info' | 'ok' | 'warn' | 'danger' {
  return tone === 'neutral' ? 'info' : tone;
}

/** Todo lo que la celda dice en palabras, en el eje que se está mirando. */
function cellTitle(cell: Cell, mode: MatrixMode): string {
  const parts: string[] = [];
  if (mode !== 'payment') {
    parts.push(`Facturación: ${BILLING_META[cell.billing_state].label}`);
    if (cell.charge_missing) parts.push('Correspondía cobrar y no se generó el cobro');
    if (cell.invoice_missing) parts.push('Falta la factura fiscal');
  }
  if (mode !== 'billing') {
    parts.push(`Pago: ${PAYMENT_META[cell.payment_state].label}`);
    if (cell.overdue) parts.push('Vencido');
  }
  if (cell.certainty === 'INCIERTO') parts.push('Sin evidencia suficiente: espera revisión');
  if (cell.discrepancy) parts.push('Hay una discrepancia');
  if (cell.note) parts.push(cell.note);
  return parts.join(' — ');
}

export function MatrixPage() {
  const currentYear = Number(today().slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const [firstYear, setFirstYear] = useState(currentYear);
  const [search, setSearch] = useState('');
  // Cobros es de lo que hoy tiene un plan que cobrar — un prospecto o uno ya
  // terminado no le corresponde a esta pantalla por defecto (dueño,
  // 13-sep-2026). El servidor igual muestra cualquier fila que pida atención,
  // esté activa o no: este filtro es de vista, nunca de deuda.
  const [onlyActive, setOnlyActive] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [mode, setMode] = useState<MatrixMode>(storedMode);
  const [open, setOpen] = useState<{ unit: Row; cell: Cell; anchor: DOMRect } | null>(null);

  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);

  const params = new URLSearchParams({ year: String(year) });
  if (search.trim()) params.set('search', search.trim());
  if (!onlyActive) params.set('only_active', '0');
  if (onlyIssues) params.set('only_discrepancies', '1');

  const query = useApi<Matrix>(`/admin/matrix?${params}`);

  // El primer año con datos no cambia según qué año se esté mirando: una vez
  // que se conoce, el selector no debería volver a angostarse mientras carga
  // el año siguiente.
  useEffect(() => {
    if (query.data && query.data.first_year !== firstYear) setFirstYear(query.data.first_year);
  }, [query.data, firstYear]);

  const years = Array.from({ length: Math.max(currentYear - firstYear + 1, 1) }, (_, i) => currentYear - i);
  const counts = query.data?.counts;
  const scope: StatusScope = onlyActive ? 'ACTIVE' : 'ALL';
  const countOf = (s: StatusScope) => (s === 'ACTIVE' ? counts?.active : counts?.all);

  return (
    <>
      <div className="row row--wrap mb-3">
        <select
          className="select"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: 110 }}
          aria-label="Año"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <input
          className="input"
          placeholder="Buscar consultorio o doctor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />

        {statusOpen ? (
          <div
            className="segmented segmented--status"
            role="group"
            aria-label="Qué consultorios mostrar"
            title={ACTIVE_SCOPE_HINT}
            style={{
              '--seg-n': STATUS_SCOPES.length,
              '--seg-i': STATUS_SCOPES.findIndex((s) => s.key === scope),
            } as React.CSSProperties}
          >
            <div className="segmented__pill" />
            {STATUS_SCOPES.map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={scope === s.key}
                className={`segmented__opt${s.tone ? ` segmented__opt--${s.tone}` : ''}${scope === s.key ? ' is-active' : ''}`}
                onClick={() => { // HAPTIC_FEEDBACK
                  if (scope !== s.key) { haptic.light(); setOnlyActive(s.key === 'ACTIVE'); }
                  setStatusOpen(false);
                }}
              >
                {s.label}
                {countOf(s.key) != null && <span className="segmented__count">{countOf(s.key)}</span>}
              </button>
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="chip-filtro"
            title={ACTIVE_SCOPE_HINT}
            aria-label={`Consultorios: ${STATUS_SCOPES.find((s) => s.key === scope)?.label}. Cambiar`}
            onClick={() => { haptic.light(); setStatusOpen(true); }} // HAPTIC_FEEDBACK
          >
            {STATUS_SCOPES.find((s) => s.key === scope)?.label}
            {countOf(scope) != null && <span className="segmented__count">{countOf(scope)}</span>}
            <Icon name="chevron-der" size={14} />
          </button>
        )}

        <button
          type="button"
          className={`chip-toggle${onlyIssues ? ' is-on' : ''}`}
          aria-pressed={onlyIssues}
          onClick={() => { haptic.light(); setOnlyIssues((v) => !v); }} // HAPTIC_FEEDBACK
        >
          <Icon name="alerta" size={14} />
          Necesita atención
          {counts && <span className="segmented__count">{counts.attention}</span>}
        </button>

        <span className="spacer" />
        <ModeSwitch mode={mode} onChange={setMode} />
      </div>

      <Async
        query={query}
        empty={(d) => d.rows.length === 0 && (
          <Empty
            icon="reportes"
            title={onlyIssues ? 'Nada que revisar en este año' : 'Sin consultorios que mostrar'}
            hint={onlyIssues ? 'Todos los meses cuadran.' : undefined}
          />
        )}
        // 12 meses + la columna ancla de consultorio: la misma cuenta de
        // columnas que la tabla real, aunque la celda de verdad sea un botón
        // de color y no texto — el esqueleto solo promete la forma.
        skeleton={<TableSkeleton rows={8} cols={13} />}
      >
        {(data) => (
          <>
            <Legend summary={data.summary} mode={mode} />

            {/* Escritorio: los doce meses de un vistazo. */}
            <div className="matrix-desktop">
              <Card flush>
                <div className="table-wrap">
                  <table className="tbl matrix">
                    <thead>
                      <tr>
                        <th className="tbl--anchor">
                          Consultorio
                        </th>
                        {data.months.map((m) => (
                          <th key={m} className="right" style={{ minWidth: 52 }}>
                            {periodLabelShort(m).split(' ')[0]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row) => (
                        <tr key={row.unit_id}>
                          <td className="strong tbl--anchor">
                            <Link to={`/admin/consultorios/${row.unit_id}`}>
                              Consultorio {row.unit_code}
                            </Link>
                            {/* Solo se ve cuando el filtro dejó pasar un consultorio
                                inactivo por la excepción de "algo pendiente": si no,
                                todas las filas son ACTIVE y el badge no dice nada. */}
                            {row.status !== 'ACTIVE' && (
                              <UnitStatusBadge status={row.status} />
                            )}
                            <span className="tbl__sub">
                              {row.location_name ?? '—'}
                              {row.attention > 0 && ` · ${row.attention} por revisar`}
                            </span>
                          </td>
                          {row.cells.map((cell) => (
                            <td key={cell.period} style={{ padding: 4 }}>
                              <CellButton
                                cell={cell}
                                mode={mode}
                                onOpen={(anchor) => setOpen({ unit: row, cell, anchor })}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            {/* Móvil: un mes a la vez. Doce columnas en 390px no se leen. */}
            <div className="matrix-mobile">
              <MobileMatrix
                data={data}
                mode={mode}
                onOpen={(unit, cell, anchor) => setOpen({ unit, cell, anchor })}
              />
            </div>
          </>
        )}
      </Async>

      {open && (
        <CellPopover
          unit={open.unit}
          cell={open.cell}
          anchor={open.anchor}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** El conmutador de eje. Mismo `.segmented` que la galería y Personal. */
function ModeSwitch({ mode, onChange }: { mode: MatrixMode; onChange: (m: MatrixMode) => void }) {
  return (
    <div
      className="segmented"
      role="group"
      aria-label="Qué eje pinta la matriz"
      style={{
        '--seg-n': MODE_OPTIONS.length,
        '--seg-i': MODE_OPTIONS.findIndex((m) => m.key === mode),
        minWidth: 260,
      } as React.CSSProperties}
    >
      <div className="segmented__pill" />
      {MODE_OPTIONS.map((m) => (
        <button
          key={m.key}
          type="button"
          aria-pressed={mode === m.key}
          className={`segmented__opt${mode === m.key ? ' is-active' : ''}`}
          onClick={() => { if (mode !== m.key) haptic.light(); onChange(m.key); }} // HAPTIC_FEEDBACK
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function CellButton({ cell, mode, onOpen }: {
  cell: Cell; mode: MatrixMode; onOpen: (anchor: DOMRect) => void;
}) {
  const tone = cellTone(cell, mode);
  const title = cellTitle(cell, mode);
  const modifier = cellModifier(cell, mode);

  return (
    <button
      type="button"
      className="matrix__cell"
      style={TONE_STYLE[tone]}
      title={title}
      aria-label={`${periodLabel(cell.period)}: ${title}`}
      onClick={(e) => onOpen(e.currentTarget.getBoundingClientRect())}
    >
      {/* En Ambos la letra chica de arriba es la facturación y el símbolo
          grande el pago. En los modos de un solo eje esa fila desaparece: si
          quedara, seguiría afirmando algo del eje que el usuario pidió no ver. */}
      {mode === 'both' && (
        <span className="matrix__cell-bill" aria-hidden="true">
          {BILLING_META[cell.billing_state].mark}
        </span>
      )}
      <span className="matrix__cell-pay" aria-hidden="true">{cellMark(cell, mode)}</span>
      {modifier && <span className="matrix__cell-mod" aria-hidden="true">{modifier.mark}</span>}
      {cell.discrepancy && <span className="matrix__cell-flag" aria-hidden="true">≠</span>}
    </button>
  );
}

/**
 * Por qué un consultorio no-activo se coló con «Solo consultorios activos»
 * marcado: la excepción de `only_active` en el servidor. Mismo mapa de tono
 * que la galería de consultorios (`UNIT_STATUS_TONE`), para no inventar una
 * paleta nueva para el mismo hecho.
 */
function UnitStatusBadge({ status }: { status: string }) {
  const key = status as UnitStatus;
  return (
    <Badge tone={UNIT_STATUS_TONE[key] ?? 'neutral'}>
      {UNIT_STATUS_LABEL[key] ?? status}
    </Badge>
  );
}

/** Una entrada de la leyenda: cuadrito con la marca, etiqueta y conteo. */
function LegendItem({ mark, label, count, tone }: {
  mark: string; label: string; count: number; tone: Tone;
}) {
  return (
    <span className="row" style={{ gap: 5 }}>
      <span className="matrix__cell matrix__cell--legend" style={TONE_STYLE[tone]}>
        <span aria-hidden="true">{mark}</span>
      </span>
      <span className="muted">{label} ({count})</span>
    </span>
  );
}

/**
 * La leyenda dice **lo que la grilla está pintando ahora**, no todo el
 * vocabulario. Mostrar los símbolos de un eje que el modo no dibuja es lo que
 * hacía que once entradas compitieran por la atención a la vez.
 */
function Legend({ summary, mode }: { summary: MatrixSummary; mode: MatrixMode }) {
  const billingStates = (Object.keys(BILLING_META) as BillingState[]).filter((s) => summary.billing[s]);
  const paymentStates = (Object.keys(PAYMENT_META) as PaymentState[]).filter((s) => summary.payment[s]);
  // Sin ninguna de las tres, el rótulo «Además:» quedaría solo, prometiendo
  // algo que no está.
  const hasExtras = summary.uncertain > 0 || summary.discrepancies > 0
    || (mode === 'both' && summary.needs_invoice > 0);

  return (
    <div className="mb-2" style={{ display: 'grid', gap: 4 }}>
      {mode !== 'payment' && (
        <div className="row row--wrap small" style={{ gap: 10 }}>
          <span className="muted" style={{ minWidth: 74 }}>Facturación:</span>
          {billingStates.map((s) => (
            <LegendItem
              key={s}
              mark={BILLING_META[s].mark}
              label={BILLING_META[s].label}
              count={summary.billing[s]!}
              tone={BILLING_META[s].tone}
            />
          ))}
          {/* Solo en el modo de facturación: en Ambos esta misma celda se lee
              en su combinación con el pago, unas líneas más abajo. */}
          {mode === 'billing' && summary.invoice_missing > 0 && (
            <LegendItem
              mark={`F${NO_INVOICE_SUFFIX}`}
              label="Falta la factura fiscal"
              count={summary.invoice_missing}
              tone="warn"
            />
          )}
        </div>
      )}

      {mode !== 'billing' && (
        <div className="row row--wrap small" style={{ gap: 10 }}>
          <span className="muted" style={{ minWidth: 74 }}>Pago:</span>
          {paymentStates.map((s) => (
            <LegendItem
              key={s}
              mark={PAYMENT_META[s].mark}
              label={PAYMENT_META[s].label}
              count={summary.payment[s]!}
              tone={PAYMENT_META[s].tone}
            />
          ))}
          {summary.overdue > 0 && (
            <LegendItem
              mark={OVERDUE_MARK}
              label="Vencido (sobre pendiente o parcial)"
              count={summary.overdue}
              tone="danger"
            />
          )}
        </div>
      )}

      {hasExtras && (
        <div className="row row--wrap small" style={{ gap: 10 }}>
          <span className="muted" style={{ minWidth: 74 }}>Además:</span>
          {summary.uncertain > 0 && (
            <LegendItem
              mark={UNCERTAIN_MARK}
              label="Sin evidencia suficiente"
              count={summary.uncertain}
              tone="warn"
            />
          )}
          {summary.discrepancies > 0 && (
            <LegendItem mark="≠" label="Discrepancias" count={summary.discrepancies} tone="danger" />
          )}
          {/* La combinación de los dos ejes. Solo tiene sentido donde se ven los
              dos: en modo Pago esa celda es «Pagado» y ya. */}
          {mode === 'both' && summary.needs_invoice > 0 && (
            <LegendItem
              mark={`✓${NO_INVOICE_SUFFIX}`}
              label="Cobrado, falta factura fiscal"
              count={summary.needs_invoice}
              tone="warn"
            />
          )}
        </div>
      )}
    </div>
  );
}

function MobileMatrix({ data, mode, onOpen }: {
  data: Matrix; mode: MatrixMode; onOpen: (unit: Row, cell: Cell, anchor: DOMRect) => void;
}) {
  const thisMonth = today().slice(0, 7);
  const initial = data.months.findIndex((m) => m.startsWith(thisMonth));
  const [index, setIndex] = useState(initial >= 0 ? initial : 0);
  const month = data.months[index]!;

  return (
    <>
      <div className="row mb-2">
        <button type="button" className="btn" onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0} aria-label="Mes anterior">←</button>
        <strong className="spacer" style={{ textAlign: 'center' }}>{periodLabel(month)}</strong>
        <button type="button" className="btn" onClick={() => setIndex((i) => Math.min(11, i + 1))}
          disabled={index === 11} aria-label="Mes siguiente">→</button>
      </div>

      <Card flush>
        <Table head={
          <tr>
            <th>Consultorio</th><th className="right">Monto</th>
            {mode !== 'payment' && <th>Facturación</th>}
            {mode !== 'billing' && <th>Pago</th>}
          </tr>
        }>
          {data.rows.map((row) => {
            const cell = row.cells[index]!;
            return (
              <tr
                key={row.unit_id}
                onClick={(e) => onOpen(row, cell, e.currentTarget.getBoundingClientRect())}
                style={{ cursor: 'pointer' }}
              >
                <td className="strong">
                  Consultorio {row.unit_code}
                  {row.status !== 'ACTIVE' && <UnitStatusBadge status={row.status} />}
                  <span className="tbl__sub">{row.location_name ?? '—'}</span>
                </td>
                <td className="right">
                  <Money value={cell.actual_minor ?? cell.expected_minor} dim={!cell.actual_minor} />
                </td>
                {mode !== 'payment' && (
                  <td><BillingBadge cell={cell} /></td>
                )}
                {mode !== 'billing' && (
                  <td><PaymentBadge cell={cell} /></td>
                )}
              </tr>
            );
          })}
        </Table>
      </Card>
    </>
  );
}

/** El eje de facturación en palabras, con sus modificadores. Su tono sale solo de la facturación. */
function BillingBadge({ cell }: { cell: Cell }) {
  const meta = BILLING_META[cell.billing_state];
  const label = cell.charge_missing ? 'Falta el cobro'
    : cell.invoice_missing ? 'Falta la factura fiscal'
      : meta.label;
  return <Badge tone={billingTone(cell)}>{label}</Badge>;
}

/** El eje de pago en palabras. Su tono sale solo del pago; incierto y discrepancia se dicen aparte. */
function PaymentBadge({ cell }: { cell: Cell }) {
  const meta = PAYMENT_META[cell.payment_state];
  return (
    <>
      <Badge tone={paymentTone(cell)}>
        {meta.label}{cell.overdue ? ' · vencido' : ''}
      </Badge>
      {cell.certainty === 'INCIERTO' && <span className="tbl__sub">Espera revisión</span>}
      {cell.discrepancy && <span className="tbl__sub">Hay una discrepancia</span>}
    </>
  );
}

/* ── Popover de edición rápida ────────────────────────────────────────────── */

interface Detail {
  expected: {
    subscription_id: string | null;
    total_minor: number; service_name: string; allocation_mode: string | null;
    skip_reason: string | null; validation: string | null;
    payers: { name: string; due_minor: number; share_basis: number | null; can_invoice: boolean }[];
    excluded_payers: { name: string; reason: string }[];
  } | null;
  charges: {
    id: string; period_start: string; unit_code: string; service_name_snapshot: string;
    total_minor: number; status: string; provenance: string; provenance_note: string | null;
  }[];
  payers: { id: string; billing_name_snapshot: string; due_minor: number; paid_minor: number; payment_status: string; invoice_status: string }[];
  payments: {
    id: string; amount_minor: number; paid_at: string; method: string; status: string;
    sender_name: string | null; billing_party_name: string | null; reference: string | null;
  }[];
  invoices: { id: string; status: string; consecutivo: string | null }[];
  external_invoices: { id: string; document_number: string; total_minor: number; issued_on: string }[];
}

/** El punto de corte donde `.popover` pasa a `position: fixed` anclado al
 *  fondo de la pantalla. Tiene que coincidir con el `@media` de `app.css`. */
const POPOVER_FIXED_MAX_WIDTH = 480;

/**
 * Dónde cabe el popover sin salirse de la pantalla. `.popover` es
 * `position: absolute` (coordenadas de página, no de viewport) para que
 * acompañe el scroll de la tabla en vez de quedar flotando lejos de la celda
 * que lo abrió — por eso se suma `scrollX`/`scrollY` al rect del botón.
 */
function popoverStyle(anchor: DOMRect): React.CSSProperties {
  const width = 330;
  const estHeight = 340;

  // En el teléfono el CSS lo ancla al fondo con `position: fixed`, o sea
  // coordenadas de VIEWPORT. Devolver acá coordenadas de PÁGINA las pisaría
  // —el inline gana— y con 900px de scroll el popover se dibujaría fuera de la
  // pantalla, con el overlay invisible comiéndose el toque siguiente. Ahí no se
  // posiciona desde JS: manda el CSS.
  if (window.innerWidth <= POPOVER_FIXED_MAX_WIDTH) {
    return { maxHeight: 'min(80vh, 520px)', overflowY: 'auto' };
  }

  const pageLeft = anchor.left + window.scrollX;
  const left = Math.min(Math.max(pageLeft, window.scrollX + 8), window.scrollX + window.innerWidth - width - 8);
  const openUp = window.innerHeight - anchor.bottom < estHeight && anchor.top > estHeight;
  const top = openUp
    ? Math.max(window.scrollY + 8, anchor.top + window.scrollY - estHeight)
    : anchor.bottom + window.scrollY + 6;
  return { top, left, maxHeight: 'min(80vh, 520px)', overflowY: 'auto' };
}

/**
 * El editor rápido de una celda: facturación y pago, cada uno con sus propios
 * botones. Ninguno inventa un estado — todos disparan la misma acción que ya
 * existía en otra pantalla (generar, anular, registrar pago, resolver
 * histórico), así que no hay una segunda lógica de negocio que mantener.
 *
 * Abrir el detalle completo reemplaza el popover por el modal — no tiene
 * sentido dejar dos superficies con el mismo detalle abiertas a la vez. Los
 * modales de acciones rápidas sí pueden convivir: su backdrop vive en una
 * capa superior y deja este panel detrás hasta cerrar la acción.
 */
function CellPopover({ unit, cell, anchor, onClose }: {
  unit: Row; cell: Cell; anchor: DOMRect; onClose: () => void;
}) {
  const period = cell.period.slice(0, 7);
  const query = useApi<Detail>(`/admin/matrix/${unit.unit_id}/${period}`);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (showFull) {
    return <CellDetail unit={unit} cell={cell} onClose={onClose} />;
  }

  return (
    <>
      <div className="popover-overlay" onClick={onClose} />
      <div className="popover" style={popoverStyle(anchor)} role="dialog" aria-modal="false"
        aria-label={`Consultorio ${unit.unit_code} · ${periodLabel(cell.period)}`}>
        <div className="popover__head">
          <h3>{unit.unit_code} · {periodLabel(cell.period)}</h3>
          <span className="spacer" />
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <Async query={query}>
            {(data) => (
              <>
                <BillingSection cell={cell} data={data} />
                <PaymentSection unit={unit} cell={cell} data={data} />
              </>
            )}
          </Async>
        </div>

        <div className="popover__foot">
          <button type="button" className="btn btn--sm" onClick={() => setShowFull(true)}>
            Ver detalle completo →
          </button>
        </div>
      </div>
    </>
  );
}

function BillingSection({ cell, data }: { cell: Cell; data: Detail }) {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  const generate = useApiMutation(
    () => api.post<{ created: number; skipped: number }>('/admin/generate', {
      period: cell.period.slice(0, 7),
      subscription_ids: cell.subscription_id ? [cell.subscription_id] : [],
    }),
    ['/admin/matrix'],
    { onSuccess: () => haptic.success() }, // HAPTIC_FEEDBACK
  );

  const voidCharge = useApiMutation(
    () => api.post(`/admin/charges/${cell.charge_id}/void`, { reason }),
    ['/admin/matrix'],
    { onSuccess: () => { haptic.warning(); setVoiding(false); setReason(''); } }, // HAPTIC_FEEDBACK
  );

  // `charge_missing` dice exactamente lo mismo que antes decía la coincidencia
  // `NO_FACTURADO` + `PENDIENTE`, pero como hecho de facturación.
  const canGenerate = cell.charge_missing && cell.subscription_id;

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="msec__title" style={{ margin: 0 }}>Facturación</span>
        <BillingBadge cell={cell} />
      </div>
      {cell.note && <p className="small muted mt-1" style={{ margin: '4px 0 0' }}>{cell.note}</p>}

      {canGenerate && (
        <div className="mt-2">
          {generate.error && <Note tone="danger">{generate.error.message}</Note>}
          {generate.data && generate.data.created === 0 && (
            <p className="small mt-1" style={{ color: 'var(--danger)' }}>
              No se generó: revisá la configuración del paquete para este mes.
            </p>
          )}
          <Button variant="primary" disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generando…' : 'Generar cobro'}
          </Button>
        </div>
      )}

      {cell.billing_state === 'FACTURADO' && cell.charge_id && (
        <div className="mt-2">
          {voidCharge.error && <Note tone="danger">{voidCharge.error.message}</Note>}
          {!voiding ? (
            <Button onClick={() => setVoiding(true)}>Anular cobro</Button>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              <Field label="Motivo de la anulación" required>
                <textarea
                  className="input" rows={2} value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Por qué se anula este cobro"
                />
              </Field>
              <div className="row">
                <Button onClick={() => { setVoiding(false); setReason(''); }}>Cancelar</Button>
                <Button
                  variant="danger"
                  disabled={reason.trim().length < 3 || voidCharge.isPending}
                  onClick={() => voidCharge.mutate()}
                >
                  {voidCharge.isPending ? 'Anulando…' : 'Confirmar anulación'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {data.expected?.subscription_id && (
        <div className="mt-2">
          <Link className="small" to={`/admin/contratos/${data.expected.subscription_id}`}>
            Editar distribución futura →
          </Link>
        </div>
      )}
    </div>
  );
}

function PaymentSection({ unit, cell, data }: { unit: Row; cell: Cell; data: Detail }) {
  const [paying, setPaying] = useState(false);
  const [resolving, setResolving] = useState(false);

  if (cell.billing_state !== 'FACTURADO') {
    return (
      <div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="msec__title" style={{ margin: 0 }}>Pago</span>
          <PaymentBadge cell={cell} />
        </div>
      </div>
    );
  }

  const soloPayer = data.payers.length === 1 ? data.payers[0] : null;
  const charge = data.charges[0];

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="msec__title" style={{ margin: 0 }}>Pago</span>
        <PaymentBadge cell={cell} />
      </div>

      {(cell.actual_minor != null) && (
        <p className="small muted mt-1" style={{ margin: '4px 0 0' }}>
          <Money value={cell.actual_minor} /> facturado · <Money value={cell.paid_minor} /> pagado
        </p>
      )}

      {cell.certainty === 'INCIERTO' && charge && (
        <div className="mt-2">
          <Button onClick={() => setResolving(true)}>Resolver</Button>
          {resolving && (
            <DecisionModal
              charge={{
                id: charge.id,
                unit_code: unit.unit_code,
                unit_name: unit.unit_name,
                period_start: charge.period_start,
                service_name_snapshot: charge.service_name_snapshot,
                total_minor: charge.total_minor,
                provenance: charge.provenance as Provenance,
                provenance_note: charge.provenance_note,
                status: charge.status,
              }}
              onClose={() => setResolving(false)}
            />
          )}
        </div>
      )}

      {cell.payment_state === 'EN_REVISION' && (
        <p className="small mt-2">
          <Link to="/admin/cobros/pagos">Ver comprobantes pendientes →</Link>
        </p>
      )}

      {/* «Vencido» ya no es un estado: un cobro vencido es uno pendiente o
          parcial con la fecha pasada, y los dos ya estaban en esta condición. */}
      {(cell.payment_state === 'PENDIENTE' || cell.payment_state === 'PARCIAL') && (
        soloPayer ? (
          <div className="mt-2">
            <Button variant="primary" onClick={() => setPaying(true)}>Registrar pago</Button>
            {paying && (
              <ManualPaymentModal
                receivable={{
                  id: soloPayer.id,
                  billing_name_snapshot: soloPayer.billing_name_snapshot,
                  due_minor: soloPayer.due_minor,
                  paid_minor: soloPayer.paid_minor,
                }}
                onClose={() => setPaying(false)}
              />
            )}
          </div>
        ) : data.payers.length > 1 ? (
          <p className="small muted mt-2">
            Este mes tiene {data.payers.length} pagadores — abrí el detalle completo para gestionarlos.
          </p>
        ) : null
      )}
    </div>
  );
}

/* ── Detalle de celda ─────────────────────────────────────────────────────── */

/**
 * Todo lo que hay detrás de un cuadrito.
 *
 * El objetivo es que la aplicación **pueda explicar de dónde sale cada valor**:
 * qué decía el contrato, qué se emitió, quién pagó y qué documentos existen.
 */
function CellDetail({ unit, cell, onClose }: { unit: Row; cell: Cell; onClose: () => void }) {
  const query = useApi<Detail>(`/admin/matrix/${unit.unit_id}/${cell.period.slice(0, 7)}`);

  return (
    <Modal
      title={`Consultorio ${unit.unit_code} · ${periodLabel(cell.period)}`}
      wide
      onClose={onClose}
      footer={
        <>
          {/* La distribución futura nunca se edita celda por celda — este
              enlace lleva al editor visual del contrato, no a un formulario
              suelto para este mes. */}
          {query.data?.expected?.subscription_id && (
            <Link
              className="btn btn--ghost"
              to={`/admin/contratos/${query.data.expected.subscription_id}`}
            >
              Editar distribución futura →
            </Link>
          )}
          <span className="spacer" />
          {cell.charge_id
            ? <Link className="btn btn--primary" to={`/admin/cobros/detalle/${cell.charge_id}`}>Abrir el cobro</Link>
            : <button type="button" className="btn" onClick={onClose}>Cerrar</button>}
        </>
      }
    >
      {/* Cada eje dice lo suyo y ninguno se pinta con el tono del otro: es el
          mismo criterio que la celda, para que el detalle no contradiga a la
          grilla de la que se abrió. */}
      <div className="row row--wrap" style={{ gap: 8 }}>
        <Note tone={noteTone(billingTone(cell))}>
          <strong>Facturación: {BILLING_META[cell.billing_state].label}.</strong>
          {cell.charge_missing && ' Correspondía cobrar y no se generó el cobro.'}
          {cell.invoice_missing && ' Falta la factura fiscal.'}
        </Note>
        <Note tone={noteTone(paymentTone(cell))}>
          <strong>
            Pago: {PAYMENT_META[cell.payment_state].label}{cell.overdue ? ', vencido' : ''}.
          </strong> {cell.note ?? 'Sin observaciones.'}
        </Note>
        {cell.certainty === 'INCIERTO' && (
          <Note tone="warn"><strong>Procedencia: sin evidencia suficiente.</strong> Espera revisión.</Note>
        )}
        {cell.discrepancy && (
          <Note tone="danger"><strong>Hay una discrepancia.</strong></Note>
        )}
      </div>

      <Async query={query}>
        {(data) => (
          <div className="mt-2">
            <div className="grid grid--2">
              <Card title="Configuración actual del paquete">
                {/* Esto es el reparto DEFAULT de hoy, no una reconstrucción de
                    lo que pasó ese mes — para eso está la tabla de Pagadores,
                    que sale del histórico reconciliado cuando existe. */}
                {data.expected ? (
                  <dl className="dl">
                    <dt>Servicio</dt><dd>{data.expected.service_name || '—'}</dd>
                    <dt>Reparto</dt><dd>{data.expected.allocation_mode ?? '—'}</dd>
                    <dt>Precio de hoy</dt><dd><Money value={data.expected.total_minor} /></dd>
                  </dl>
                ) : <p className="muted small">Sin paquete para este período.</p>}

                {data.expected?.skip_reason && (
                  <p className="small muted mt-2">{data.expected.skip_reason}</p>
                )}

                {(data.expected?.excluded_payers.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <div className="msec__title">No les correspondía</div>
                    {data.expected!.excluded_payers.map((e) => (
                      <div key={e.name} className="small muted">
                        {e.name} — {e.reason}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Lo que se emitió">
                {data.charges.length === 0 ? (
                  <p className="muted small">No se generó ningún cobro.</p>
                ) : data.charges.map((ch) => (
                  <dl className="dl" key={ch.id}>
                    <dt>Total</dt><dd><Money value={ch.total_minor} /></dd>
                    <dt>Estado</dt><dd>{ch.status}</dd>
                    <dt>Origen</dt><dd>{ch.provenance}</dd>
                  </dl>
                ))}
              </Card>
            </div>

            {data.payers.length > 0 && (
              <div className="mt-3">
                {/* «Nombre» es el responsable del período — de dónde salió cada
                    fila del histórico, no el reparto default del paquete. */}
                <Card title="Pagadores (responsable del período)" flush>
                  <Table head={
                    <tr>
                      <th>Nombre</th><th className="right">Debe</th><th className="right">Pagó</th>
                      <th>Estado de pago</th><th>Estado de factura</th>
                    </tr>
                  }>
                    {data.payers.map((p) => (
                      <tr key={p.id}>
                        <td>{p.billing_name_snapshot}</td>
                        <td className="right"><Money value={p.due_minor} /></td>
                        <td className="right"><Money value={p.paid_minor} dim={!p.paid_minor} /></td>
                        <td className="small">{p.payment_status}</td>
                        <td className="small muted">{p.invoice_status}</td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>
            )}

            {data.payments.length > 0 && (
              <div className="mt-3">
                {/* Quién mandó la plata de verdad puede no ser el responsable
                    de arriba — son ejes distintos, y acá se ven separados. */}
                <Card title="Pagos (quién mandó la plata)" flush>
                  <Table head={
                    <tr>
                      <th className="right">Monto</th><th>Fecha</th><th>Remitente</th>
                      <th>Billing party</th><th>Nota</th>
                    </tr>
                  }>
                    {data.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="right"><Money value={p.amount_minor} /></td>
                        <td className="small muted">{p.paid_at.slice(0, 10)}</td>
                        <td className="small">{p.sender_name ?? '—'}</td>
                        <td className="small">{p.billing_party_name ?? '—'}</td>
                        <td className="small muted">{p.reference ?? '—'}</td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>
            )}

            {data.external_invoices.length > 0 && (
              <div className="mt-3">
                <Card title="Documentos fiscales del histórico" flush>
                  <Table head={<tr><th>Documento</th><th>Emitido</th><th className="right">Total</th></tr>}>
                    {data.external_invoices.map((f) => (
                      <tr key={f.id}>
                        <td className="mono small">{f.document_number}</td>
                        <td className="small muted">{f.issued_on}</td>
                        <td className="right"><Money value={f.total_minor} /></td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>
            )}
          </div>
        )}
      </Async>
    </Modal>
  );
}
